/**
 * Batch 27c — Super-admin login UI on admin.certainhr.ai.
 * Batch 27f — visual port to AuthShell so the admin gate matches the
 * tenant-side brand surface.
 *
 * Mirrors the visual idioms of pages/auth/Login.tsx (the tenant login form)
 * but strips the tenant_slug field — super admins have tenant_id=NULL per
 * migration 0023 and don't belong to any tenant. Backend `/auth/login`
 * dispatches by host header to `_admin_login` (app/api/auth.py:676), which
 * accepts `{email, password}` and issues a session via the admin cookie
 * domain.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminLogin } from "@/hooks/queries/auth/useAdminLogin";
import { Button } from "@/components/common/Button";
import { FormField } from "@/components/common/FormField";
import { Input } from "@/components/common/Input";
import { AuthShell } from "@/components/layout/AuthShell";
import { isMfaPendingResponse } from "@/lib/loginResponse";
import { MfaVerifyModal } from "@/components/auth/MfaVerifyModal";
import { useAuthStore } from "@/stores/useAuthStore";

export default function AdminLogin(): JSX.Element {
  const navigate = useNavigate();
  const login = useAdminLogin();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  // P0 passkey-v2 loop fix: the MFA challenge is now a MODAL owned by this
  // page, opened on an ``mfa_pending`` login response. It replaces the old
  // ``/admin/login/mfa-verify`` + ``mfa-enroll`` routes whose
  // navigate-on-null-token effect caused the login loop.
  const [showMfaModal, setShowMfaModal] = useState(false);

  // HARD-9: every navigation to /admin/login wipes any stale
  // mfa_pending slot from a previous in-flight login attempt.
  useEffect(() => {
    useAuthStore.getState().mfaPendingClear();
  }, []);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFieldError(null);

    login.mutate(
      { email, password },
      {
        onSuccess: (data) => {
          if (isMfaPendingResponse(data)) {
            // The login hook already stashed the mfa_pending token in the
            // store (onSuccess in useAdminLogin). Open the modal — it renders
            // the enroll or verify surface off ``mfaPendingNextStep``. No
            // route change; the URL stays on /admin/login.
            setShowMfaModal(true);
            return;
          }
          navigate("/admin/tenants", { replace: true });
        },
        onError: (err) => {
          switch (err.error) {
            case "invalid_credentials":
              setFieldError("Email or password incorrect.");
              break;
            case "too_many_attempts": {
              const retryAfter = typeof err.details["retry_after_seconds"] === "number"
                ? err.details["retry_after_seconds"]
                : null;
              const minutes = retryAfter ? Math.ceil(retryAfter / 60) : null;
              setFieldError(
                `Account temporarily locked. ${minutes ? `Try again in ${minutes} minute${minutes === 1 ? "" : "s"}.` : "Try again later."}`
              );
              break;
            }
            case "validation_error":
              setFieldError("Check the email and password fields.");
              break;
            default:
              setFieldError("That didn't go through. Try again.");
          }
        },
      }
    );
  };

  return (
    <AuthShell title="Super Admin" subtitle="Sign in to the admin console.">
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormField label="Email" htmlFor="email">
          <Input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            hasError={!!fieldError}
          />
        </FormField>
        <FormField label="Password" htmlFor="password" error={fieldError ?? undefined}>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            hasError={!!fieldError}
          />
        </FormField>
        <Button type="submit" className="w-full" loading={login.isPending}>
          Sign in
        </Button>
      </form>
      {showMfaModal ? (
        <MfaVerifyModal
          variant="admin"
          onComplete={() => navigate("/admin/tenants", { replace: true })}
          onClose={() => {
            // Dismiss / expiry — drop the pending slot + close the modal.
            // NO navigate: we stay on the login form.
            useAuthStore.getState().mfaPendingClear();
            setShowMfaModal(false);
          }}
        />
      ) : null}
    </AuthShell>
  );
}
